import React from 'react';
import { Sparkles, Heart, Wind, Droplets, FlaskConical, ShieldAlert } from 'lucide-react';
import { Separator } from './ui/separator';

export function CareInstructionsPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-brand-green text-white py-16 px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center mb-6 animate-in fade-in zoom-in duration-700">
            <Heart className="h-16 w-16 text-brand-gold" />
          </div>
          <h1 className="text-4xl font-serif mb-4 animate-in fade-in slide-in-from-bottom-4 duration-700">
            Care for Your <span className="text-brand-gold">Heirlooms</span>
          </h1>
          <p className="text-brand-gold/80 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
            Expert tips to keep your jewellery shining for generations.
          </p>
        </div>
      </section>

      {/* Main Care Grid */}
      <section className="py-20 px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          {/* Gold Care */}
          <div className="bg-off-white p-10 rounded-3xl border border-border animate-in fade-in slide-in-from-left-8 duration-1000">
            <h2 className="text-3xl font-serif text-brand-green mb-6 flex items-center">
              <Sparkles className="mr-3 text-brand-gold" /> Gold Care
            </h2>
            <div className="space-y-6">
              <div>
                <h4 className="font-bold text-brand-green mb-2">Regular Cleaning</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Soak your gold jewellery in a bowl of lukewarm water with a few drops of mild detergent. Use a soft-bristled brush to gently clean the crevices.
                </p>
              </div>
              <Separator />
              <div>
                <h4 className="font-bold text-brand-green mb-2">Avoid Polishing</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Excessive professional polishing can wear down the metal over years. Only polish when necessary to restore original lustre.
                </p>
              </div>
            </div>
          </div>

          {/* Silver Care */}
          <div className="bg-brand-green/5 p-10 rounded-3xl border border-brand-green/10 animate-in fade-in slide-in-from-right-8 duration-1000">
            <h2 className="text-3xl font-serif text-brand-green mb-6 flex items-center">
              <Wind className="mr-3 text-brand-green" /> Silver Care
            </h2>
            <div className="space-y-6">
              <div>
                <h4 className="font-bold text-brand-green mb-2">Tarnish Prevention</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Silver reacts with moisture and sulphur in the air. Wear your silver often—the natural oils in your skin help prevent tarnishing!
                </p>
              </div>
              <Separator />
              <div>
                <h4 className="font-bold text-brand-green mb-2">Proper Storage</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Store silver in airtight bags or velvet pouches. Adding silica gel packets or anti-tarnish strips can significantly slow down oxidation.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* The "Avoid" Section */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-serif text-brand-green mb-2">General Precautions</h2>
          <p className="text-muted-foreground uppercase tracking-widest text-xs">Essential rules for all precious metals</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              icon: Droplets,
              title: "Last On, First Off",
              desc: "Apply perfumes, hairsprays, and lotions <strong>before</strong> putting on your jewellery. Take it off <strong>first</strong> when undressing."
            },
            {
              icon: FlaskConical,
              title: "Chemical Alert",
              desc: "Remove jewellery before swimming (chlorine), cleaning (bleach), or using harsh beauty products."
            },
            {
              icon: ShieldAlert,
              title: "Safe Storage",
              desc: "Keep pieces separate to avoid scratching. Diamonds can scratch gold and other gemstones easily."
            }
          ].map((item, i) => (
            <div key={i} className="text-center p-8 bg-white rounded-2xl shadow-sm border border-border group hover:border-brand-gold transition-colors">
              <div className="h-14 w-14 bg-off-white rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <item.icon className="h-7 w-7 text-brand-green" />
              </div>
              <h3 className="text-xl font-serif text-brand-green mb-3">{item.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Professional Service Section */}
      <section className="py-20 px-6 bg-brand-green text-white text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-serif text-brand-gold mb-6">Professional Maintenance</h2>
          <p className="text-white/80 leading-relaxed mb-8">
            We recommend visiting our Alwar store once a year for a professional checkup. We'll inspect prongs, stones, and provide a deep ultrasonic cleaning to keep your RPK jewellery in pristine condition.
          </p>
          <div className="inline-block px-8 py-3 border-2 border-brand-gold text-brand-gold font-bold rounded-xl hover:bg-brand-gold hover:text-brand-green transition-colors cursor-pointer">
            Visit Our Store
          </div>
        </div>
      </section>
    </div>
  );
}
